"use client";

import type { ReactNode } from "react";
import { HoverCard } from "@astryxdesign/core/HoverCard";
import { Icon } from "@astryxdesign/core/Icon";
import { researchIcons } from "@/lib/icons/research-icons";
import {
  getAccentPreviewColor,
  resolveLandingThemeLabels,
  surfacePreviewForMode,
  type LandingPageTheme,
} from "@/lib/landing/puck/themes";

type LandingThemeSummaryChipProps = {
  theme: LandingPageTheme;
  /** toolbar — компактный чип в шапке; panel — блок в сайдбаре */
  variant?: "toolbar" | "panel";
};

function Swatch({
  color,
  border,
  title,
}: {
  color: string;
  border?: string;
  title: string;
}) {
  return (
    <span
      title={title}
      className="inline-block h-3.5 w-3.5 shrink-0 rounded-full border border-black/15 shadow-sm"
      style={{
        background: color,
        boxShadow: border ? `inset 0 0 0 1px ${border}` : undefined,
      }}
    />
  );
}

function DetailRow({
  swatch,
  label,
  value,
}: {
  swatch: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="grid grid-cols-[1rem_minmax(0,1fr)] items-center gap-x-2.5">
      <div className="flex items-center justify-center">{swatch}</div>
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-xs leading-tight text-secondary">{label}</span>
        <span className="text-sm font-semibold leading-snug text-primary">
          {value}
        </span>
      </div>
    </div>
  );
}

function ThemeDetails({
  theme,
  showHint,
}: {
  theme: LandingPageTheme;
  showHint?: boolean;
}) {
  const labels = resolveLandingThemeLabels(theme);
  const accent = getAccentPreviewColor(theme.accentPalette);
  const light = surfacePreviewForMode(theme.surfaceLight, false);
  const dark = surfacePreviewForMode(theme.surfaceDark, true);

  return (
    <div className="flex w-[16rem] flex-col gap-3 p-1">
      <DetailRow
        label="Акцент"
        value={labels.accent}
        swatch={<Swatch color={accent} title={labels.accent} />}
      />
      <DetailRow
        label="Светлая поверхность"
        value={labels.light}
        swatch={
          <Swatch
            color={light.background}
            border={light.foreground}
            title={labels.light}
          />
        }
      />
      <DetailRow
        label="Тёмная поверхность"
        value={labels.dark}
        swatch={
          <Swatch
            color={dark.background}
            border={dark.foreground}
            title={labels.dark}
          />
        }
      />
      <DetailRow
        label="Для посетителя"
        value={labels.mode}
        swatch={
          <span className="inline-flex h-3.5 w-3.5 items-center justify-center text-secondary">
            <Icon
              icon={
                theme.colorScheme === "dark"
                  ? researchIcons.moon
                  : theme.colorScheme === "light"
                    ? researchIcons.sun
                    : researchIcons.monitor
              }
              size="sm"
            />
          </span>
        }
      />
      {showHint ? (
        <p className="border-t border-border pt-2.5 text-xs leading-snug text-secondary">
          Превью в редакторе — сразу. На сайте — после сохранения у
          опубликованной страницы или после «Опубликовать».
        </p>
      ) : null}
    </div>
  );
}

export function LandingThemeSummaryChip({
  theme,
  variant = "toolbar",
}: LandingThemeSummaryChipProps) {
  const labels = resolveLandingThemeLabels(theme);
  const accent = getAccentPreviewColor(theme.accentPalette);
  const light = surfacePreviewForMode(theme.surfaceLight, false);
  const dark = surfacePreviewForMode(theme.surfaceDark, true);
  const isPanel = variant === "panel";

  const trigger = (
    <button
      type="button"
      className={[
        "group inline-flex max-w-full items-center gap-2 rounded-md border border-border bg-canvas text-left transition-colors hover:border-primary/40 hover:bg-muted/60",
        isPanel ? "w-full px-2.5 py-2" : "px-2 py-1",
      ].join(" ")}
      aria-label={`Тема: ${labels.accentShort}, ${labels.modeShort}`}
    >
      <span className="flex shrink-0 items-center -space-x-1">
        <Swatch color={accent} title={labels.accent} />
        <Swatch
          color={light.background}
          border={light.foreground}
          title={`Светлая: ${labels.light}`}
        />
        <Swatch
          color={dark.background}
          border={dark.foreground}
          title={`Тёмная: ${labels.dark}`}
        />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-medium text-primary">
          {labels.accentShort}
        </span>
        {isPanel ? (
          <span className="block truncate text-[11px] text-secondary">
            {labels.modeShort} · светлая / тёмная
          </span>
        ) : (
          <span className="ms-1 hidden text-[11px] text-secondary xl:inline">
            · {labels.modeShort}
          </span>
        )}
      </span>
      <span className="shrink-0 rotate-90 opacity-50 transition-opacity group-hover:opacity-100">
        <Icon
          icon={researchIcons.chevronRight}
          size="sm"
          color="secondary"
        />
      </span>
    </button>
  );

  return (
    <HoverCard
      content={<ThemeDetails theme={theme} showHint={isPanel} />}
      placement={isPanel ? "above" : "below"}
      alignment={isPanel ? "start" : "end"}
      delay={120}
      hideDelay={120}
      hasHoverIndication={false}
    >
      {trigger}
    </HoverCard>
  );
}
